import { Flex, Icon, Text, useTheme } from "@wayflyer/flyui";
import { RhfDropdownField } from "@wayflyer/flyui-hook-form";
import { IconCheckCircle16Line } from "@wayflyer/flyui-icons/16/line";
import { WayflyerSchemas } from "@wayflyer/api-types";
import { useState } from "react";
import type { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";
import { useIntl } from "react-intl";

import { DEFAULT_MEMBER_ROLE } from "../member-roles/member-roles-helpers";

import { companyRoleLabels, companyRoleOptions, companyRolePermissionsMap } from "./member-roles-helpers";
import { ROLE_MESSAGES } from "./messages";

export type MemberRolesFormValues = {
  role: WayflyerSchemas.CompanyRole;
};

interface RoleComponentProps<T extends FieldValues, TTransformedValues extends FieldValues = T> {
  form: UseFormReturn<T, unknown, TTransformedValues>;
  initialRole?: WayflyerSchemas.CompanyRole;
  isMemberCurrentUser?: boolean;
  isMemberLastAdmin?: boolean;
}

interface DisplayRolePermissionsComponentProps {
  selectedRole?: WayflyerSchemas.CompanyRole;
}

const DisplayRolePermissions = ({ selectedRole }: DisplayRolePermissionsComponentProps) => {
  const theme = useTheme();
  const { formatMessage: intlMsg } = useIntl();

  return selectedRole ? (
    <Flex direction="column" gap="1">
      <Text>{intlMsg(ROLE_MESSAGES.thisRoleIsAbleToText)}:</Text>
      {companyRolePermissionsMap[selectedRole].map((permission) => (
        <Flex key={permission} gap="3" align="center">
          <Icon color={theme.palette.content.status.info}>
            <IconCheckCircle16Line />
          </Icon>
          <Text>{permission}</Text>
        </Flex>
      ))}
    </Flex>
  ) : null;
};

export const MemberRolesComponent = <T extends FieldValues, TTransformedValues extends FieldValues = T>({
  form,
  initialRole = DEFAULT_MEMBER_ROLE,
  isMemberCurrentUser = false,
  isMemberLastAdmin = false,
}: RoleComponentProps<T, TTransformedValues>) => {
  const [selectedRole, setSelectedRole] = useState(initialRole);
  const { formatMessage: intlMsg } = useIntl();
  const isCurrentUsersRoleChangingFromAdmin =
    isMemberCurrentUser && selectedRole && initialRole === "admin" && selectedRole !== "admin";

  return (
    <Flex direction="column" gap="4">
      <RhfDropdownField
        label={intlMsg(ROLE_MESSAGES.role)}
        helperText={intlMsg(ROLE_MESSAGES.teamMembersRoleDescriptionText)}
        form={form}
        options={companyRoleOptions}
        initialValue={initialRole}
        name={"role" as FieldPath<T>}
        onChange={(id) => setSelectedRole(String(id) as WayflyerSchemas.CompanyRole)}
        disabled={isMemberLastAdmin}
      />
      <DisplayRolePermissions selectedRole={selectedRole} />
      {isCurrentUsersRoleChangingFromAdmin ? (
        <Text color="critical" fontWeight="semiBold">
          {intlMsg(ROLE_MESSAGES.warningWhenUpdatingAdminRole, { selectedRole: companyRoleLabels[selectedRole] })}
        </Text>
      ) : null}
      {isMemberLastAdmin ? (
        <Text color="warning" fontWeight="semiBold">
          {intlMsg(ROLE_MESSAGES.warningWhenMemberIsLastAdmin, { role: companyRoleLabels[initialRole] })}
        </Text>
      ) : null}
    </Flex>
  );
};
